import { NotificationProvider } from './contexts/NotificationContext';
import theme from './theme';
import { env } from '@/lib/env';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Loading from 'components/Basic/Loading';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { Suspense, lazy } from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router';
import ErrorPage from 'views/ErrorPage';
import SubscriptionsPage from 'views/SubscriptionsPage';

dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  weekStart: 1
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 300000,
      retry: false
    }
  }
});
const isLocal = env.DEV;

const Layout = lazy(() => import('layouts/Layout'));
const DashboardPage = lazy(() => import('views/DashboardPage'));
const CustomersPage = lazy(() => import('views/CustomersPage'));
const CustomerDetailsPage = lazy(() => import('views/CustomerDetailsPage'));
const ItemsPage = lazy(() => import('views/ItemsPage'));
const OrdersPage = lazy(() => import('views/OrdersPage'));
const MapPage = lazy(() => import('views/map-page').then(module => ({ default: module.MapPage })));
const DeliveryGroupsPage = lazy(() =>
  import('views/delivery-groups-page').then(module => ({ default: module.DeliveryGroupsPage }))
);
const PlanningSettingsPage = lazy(() =>
  import('views/planning-settings-page').then(module => ({
    default: module.PlanningSettingsPage
  }))
);
const MissedOrdersPage = lazy(() =>
  import('views/missed-orders-page').then(module => ({ default: module.MissedOrdersPage }))
);
const PaymentsPage = lazy(() =>
  import('views/payments-page').then(module => ({ default: module.PaymentsPage }))
);
const ReceiptInboxPage = lazy(() =>
  import('views/receipt-inbox-page').then(module => ({ default: module.ReceiptInboxPage }))
);
const AgentLogPage = lazy(() =>
  import('views/agent-log-page').then(module => ({ default: module.AgentLogPage }))
);
const AgentOrdersPage = lazy(() =>
  import('views/agent-orders-page').then(module => ({ default: module.AgentOrdersPage }))
);
const DataCleansingPage = lazy(() =>
  import('views/data-cleansing-page').then(module => ({ default: module.DataCleansingPage }))
);
const DataCleansingRowsPage = lazy(() =>
  import('views/data-cleansing-rows-page').then(module => ({
    default: module.DataCleansingRowsPage
  }))
);
const RoutePlanningPage = lazy(() =>
  import('views/route-planning-page').then(module => ({ default: module.RoutePlanningPage }))
);

export const App = () => {
  const router = createBrowserRouter([
    {
      path: '/',
      element: isLocal ? (
        <>
          <Layout />
          <ReactQueryDevtools />
        </>
      ) : (
        <Layout />
      ),
      errorElement: <ErrorPage />,
      children: [
        {
          path: '',
          element: <Navigate to="/dashboard" replace />
        },
        {
          path: 'dashboard',
          element: <DashboardPage />
        },
        {
          path: 'customers',
          element: <CustomersPage />
        },
        {
          path: 'customers/:id',
          element: <CustomerDetailsPage />
        },
        {
          path: 'items',
          element: <ItemsPage />
        },
        {
          path: 'orders',
          element: <OrdersPage />
        },
        {
          path: 'subscriptions',
          element: <SubscriptionsPage />
        },
        {
          path: 'map',
          element: <MapPage />
        },
        {
          path: 'delivery-groups',
          element: <DeliveryGroupsPage />
        },
        {
          path: 'planning-settings',
          element: <PlanningSettingsPage />
        },
        {
          path: 'missed-orders',
          element: <MissedOrdersPage />
        },
        {
          path: 'payments',
          element: <PaymentsPage />
        },
        {
          path: 'receipt-review',
          element: <ReceiptInboxPage />
        },
        {
          path: 'agent-log',
          element: <AgentLogPage />
        },
        {
          path: 'agent-orders',
          element: <AgentOrdersPage />
        },
        {
          path: 'data-cleansing',
          element: <DataCleansingPage />
        },
        {
          path: 'data-cleansing/:key',
          element: <DataCleansingRowsPage />
        },
        {
          path: 'route-planning',
          element: <RoutePlanningPage />
        }
      ]
    }
  ]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <QueryClientProvider client={queryClient}>
          <NotificationProvider>
            <Suspense fallback={<Loading />}>
              <RouterProvider router={router} />
            </Suspense>
          </NotificationProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};
